import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { useTranslation } from 'common/utils/getLocalString';

import {
  getGanttAccessRoles,
  getTimesheetsSettings,
  saveGanttAccessRoles,
  saveTimesheetsSettings,
} from '../../../../packages/common/api';
import Page from '../../../../packages/common/shared-ui/src/components/Page';
import CheckboxIcon from '../../../../packages/common/shared-ui/src/icons/CheckboxIcon';
import CheckboxIconChecked from '../../../../packages/common/shared-ui/src/icons/CheckboxIconChecked';
import ChevronIcon from '../../../../packages/common/shared-ui/src/icons/ChevronIcon';
import DeleteIcon from '../../../../packages/common/shared-ui/src/icons/DeleteIcon';

// Original class: filterSelect
const StyledFilterSelect = styled(Autocomplete, {
  shouldForwardProp: prop => !['multipleStyle'].includes(prop),
})(({ theme, multipleStyle }) => ({
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    padding: '8px 65px 8px 16px',
    lineHeight: '20px',
    height: 'auto',
    ...(multipleStyle && {
      minHeight: '36px',
      padding: '0 65px 0 0',
    }),
    '& .MuiAutocomplete-endAdornment': {
      '& .MuiButtonBase-root.MuiIconButton-root.MuiAutocomplete-clearIndicator':
        {
          '& .MuiSvgIcon-root': {
            width: '20px',
            height: '20px',
          },
        },
      '& .MuiSvgIcon-root': {
        width: '24px',
        height: '24px',
        position: 'static',
      },
    },
    '& .MuiAutocomplete-input': {
      fontSize: '12px',
      lineHeight: '20px',
      color: '#212346',
      padding: '0',
      height: 'auto',
      ...(multipleStyle && {
        marginLeft: '16px',
      }),
    },
    '&.Mui-disabled': {
      '& .MuiAutocomplete-input': {
        '&::placeholder': {
          color: '#E4E4EF',
        },
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#E4E4EF',
      },
      '& .MuiAutocomplete-endAdornment': {
        '& .MuiSvgIcon-root path': {
          fill: '#E4E4EF',
        },
      },
    },
  },

  '&.MuiAutocomplete-hasClearIcon': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderColor: '#3448FF',
      '&:hover': {
        borderColor: '#3448FF',
      },
    },
  },

  '& .MuiFormLabel-root.MuiInputLabel-root': {
    color: '#7174AC',
    fontSize: '14px',
    lineHeight: '20px',
    top: '-5px',
    zIndex: 99,
    '&.MuiFormLabel-filled': {
      display: 'none',
    },
  },

  '&.Mui-expanded': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderRadius: '16px 16px 0 0',
      '& .MuiOutlinedInput-notchedOutline': {
        padding: 0,
      },
    },
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '&.Mui-focused': {
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '& + .MuiAutocomplete-popper': {
    minWidth: 'auto!important',
    width: '100%!important',
    '& .MuiPaper-root.MuiAutocomplete-paper': {
      borderRadius: '0 0 16px 16px',
      border: '1px solid #E4E4EF',
      borderTop: 'none',
      boxShadow: '0px 5px 10px -8px #A0A1AA66',
      '& .MuiAutocomplete-noOptions': {
        padding: '8px 16px',
        color: '#212346',
        fontSize: '12px',
        lineHeight: '20px',
      },
      '& .MuiAutocomplete-listbox': {
        padding: '0',
        '& li': {
          whiteSpace: 'normal',
          fontSize: '12px',
          lineHeight: '20px',
          color: '#212346',
          '&.Mui-focused': {
            backgroundColor: 'transparent',
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: '#DCDFF4',
          },
        },
        '&::-webkit-scrollbar': {
          width: '2px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#7174AC',
          borderRadius: '5px',
        },
      },
    },
  },
}));
// Original class: chip
const StyledChip = styled(Chip)(theme => ({
  '&.MuiChip-root': {
    height: '30px',
    margin: '3px 4px',
    fontSize: '12px',
    display: 'flex',
    border: '1px solid #E4E4EF',
    borderRadius: '14px',
    '& .MuiChip-label': {
      padding: '0 8px',
    },
    '& .MuiChip-deleteIcon': {
      position: 'static',
      height: '10px !important',
    },
  },
}));
// Original class: blockWrapper
const StyledBlockWrapper = styled(Box)(theme => ({
  width: '100%',
  padding: '24px 32px',
  borderRadius: '16px',
  backgroundColor: '#fff',
}));
// Original class: selectLabel
const StyledSelectLabel = styled(InputLabel)(theme => ({
  '&.MuiFormLabel-root.MuiInputLabel-root': {
    position: 'static',
    width: '200px',
    color: '#41424E',
    lineHeight: '20px',
    transform: 'none',
    display: 'flex',
    flexShrink: 0,
    '& .MuiFormLabel-asterisk': {
      color: '#E03737',
    },
    '&.Mui-focused': {
      transform: 'none',
      left: '10px',
      top: '15px',
    },
  },
}));

const TimesheetsSettings = () => {
  const getLocalString = useTranslation();

  const pageTitle = getLocalString('AdminTimesheetsSettings', 'title');
  const navigate = useNavigate();
  const [timesheetsSettingsFields, setTimesheetsSettingsFields] = useState([]);
  const [initialTimesheetsSettingsFields, setInitialTimesheetsSettingsFields] =
    useState([]);
  const [loading, setLoading] = useState(true);
  const [saveSettingsloading, setSaveSettingsloading] = useState(false);
  const [settingsChanged, setSettingsChanged] = useState(false);
  const handleIssueError = useCallback(
    error => {
      if (error) {
        navigate(`/errors/error-${error.status}`);
      }
    },
    [navigate],
  );

  useEffect(() => {
    let ignore = false;
    async function getSettings() {
      const { data: settings } = await getTimesheetsSettings();
      if (!ignore) {
        return settings;
      }
    }
    getSettings()
      .then(res => {
        setTimesheetsSettingsFields(res.standards);
        setInitialTimesheetsSettingsFields(res.standards);
        setLoading(false);
      })
      .catch(error => handleIssueError(error.response));
    return () => {
      ignore = true;
    };
  }, [handleIssueError]);

  const saveConfirmationSettings = useCallback(async () => {
    setSaveSettingsloading(true);
    const data = timesheetsSettingsFields
      .filter(field => field.changed)
      .reduce((acc, item) => {
        return {
          ...acc,
          [item.fieldId]: item.valueId.map(item => Number(item.valueId)),
        };
      }, {});
    try {
      const { data: settings } = await saveTimesheetsSettings(data);
      if (settings) {
        setTimesheetsSettingsFields(settings.standards);
        setInitialTimesheetsSettingsFields(settings.standards);
        setSaveSettingsloading(false);
        setSettingsChanged(false);
      }
    } catch (error) {
      setSaveSettingsloading(false);
      handleIssueError(error.response);
    }
  }, [handleIssueError, timesheetsSettingsFields]);

  const handleCancel = useCallback(() => {
    setTimesheetsSettingsFields(initialTimesheetsSettingsFields);
    setSettingsChanged(false);
  }, [initialTimesheetsSettingsFields]);

  const handleField = useCallback(
    (data, field) => {
      setSettingsChanged(true);
      let updatedObject;
      const pos = timesheetsSettingsFields
        .map(e => e.fieldId)
        .indexOf(field.fieldId);
      updatedObject = {
        ...timesheetsSettingsFields[pos],
        valueId: data,
        valueName: data,
        changed: true,
      };
      const newFields = [
        ...timesheetsSettingsFields.slice(0, pos),
        updatedObject,
        ...timesheetsSettingsFields.slice(pos + 1),
      ];
      setTimesheetsSettingsFields(newFields);
    },
    [timesheetsSettingsFields],
  );

  return (
    <>
      {loading ? (
        <Grid
          container
          alignItems='center'
          justifyContent='center'
          width='100%'
          height='70px'
        >
          <CircularProgress color='secondary' />
        </Grid>
      ) : (
        <Page title={pageTitle} width='100%'>
          <Grid
            container
            alignItems={'center'}
            justifyContent={'space-between'}
            position={'relative'}
          >
            {saveSettingsloading && (
              <Grid
                container
                alignItems='center'
                justifyContent='center'
                width='100%'
                height='100%'
                zIndex={99}
                position={'absolute'}
              >
                <CircularProgress color='secondary' />
              </Grid>
            )}
            <Grid item>
              <Typography m={0} padding={'10px 0'} variant='h1'>
                {getLocalString('AdminTimesheetsSettings', 'title')}
              </Typography>
            </Grid>
            <Grid item>
              <Button
                variant='defaultGreyPrimary'
                disableRipple
                onClick={saveConfirmationSettings}
                disabled={!settingsChanged}
              >
                <Typography variant='h4' fontWeight={700}>
                  {getLocalString('buttons', 'save')}
                </Typography>
              </Button>
              <Button
                onClick={handleCancel}
                variant='defaultGreyPrimary'
                disableRipple
                sx={{ marginLeft: '8px' }}
              >
                <Typography variant='h4' fontWeight={700}>
                  {getLocalString('buttons', 'cancel')}
                </Typography>
              </Button>
            </Grid>
          </Grid>
          <Grid container spacing={5} paddingTop={'20px'} marginBottom={'20px'}>
            <Grid container spacing={5} size={6} flexDirection={'column'}>
              {timesheetsSettingsFields
                .filter((item, index) => index < 2)
                .map((field, index) => (
                  <Grid key={index} item>
                    <StyledBlockWrapper padding={'20px'}>
                      <FormControl
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          flex: 1,
                        }}
                      >
                        <StyledSelectLabel sx={{ width: '100%!important' }}>
                          <Typography variant='h5' color={'#41424E'}>
                            {field.fieldName}
                          </Typography>
                        </StyledSelectLabel>
                        <StyledFilterSelect
                          multipleStyle={true}
                          multiple
                          onChange={(event, newValue) =>
                            handleField(newValue, field)
                          }
                          noOptionsText={getLocalString('labels', 'notFound')}
                          options={field.fieldDefinition.values}
                          getOptionLabel={option => option.valueName || ''}
                          disableClearable
                          disablePortal
                          value={
                            !field.changed &&
                            field.fieldDefinition.values.filter(
                              item => item.isChecked,
                            ).length > 0
                              ? field.fieldDefinition.values.filter(
                                  item => item.isChecked,
                                )
                              : field.valueName
                                ? field.valueName
                                : []
                          }
                          renderValue={(value, getTagProps) =>
                            value.map((option, index) => (
                              <StyledChip
                                {...getTagProps({ index })}
                                key={index}
                                deleteIcon={
                                  <DeleteIcon
                                    width={10}
                                    height={10}
                                    viewBox='0 0 10 10'
                                  />
                                }
                                variant='outlined'
                                label={option.valueName}
                              />
                            ))
                          }
                          renderOption={(props, option, { selected }) => (
                            <li {...props} key={option.valueId}>
                              <Checkbox
                                icon=<CheckboxIcon
                                  width='12'
                                  height='12'
                                  viewBox='0 0 12 12'
                                />
                                checkedIcon=<CheckboxIconChecked
                                  width='12'
                                  height='12'
                                  viewBox='0 0 12 12'
                                />
                                style={{ marginRight: 8 }}
                                checked={selected}
                              />
                              {option.valueName}
                            </li>
                          )}
                          renderInput={params => (
                            <TextField
                              {...params}
                              placeholder={getLocalString('labels', 'input')}
                            />
                          )}
                          slots={{
                            popper: props => (
                              <Popper
                                {...props}
                                sx={{ maxWidth: '100%!important' }}
                                placement='bottom-start'
                              ></Popper>
                            ),
                          }}
                          popupIcon={
                            <ChevronIcon direction='down'></ChevronIcon>
                          }
                        />
                      </FormControl>
                    </StyledBlockWrapper>
                  </Grid>
                ))}
            </Grid>
            <Grid size={6}>
              {timesheetsSettingsFields
                .filter((item, index) => index === 2)
                .map((field, index) => (
                  <Grid key={index} item>
                    <StyledBlockWrapper padding={'20px'}>
                      <FormControl
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          flex: 1,
                        }}
                      >
                        <StyledSelectLabel sx={{ width: '100%!important' }}>
                          <Typography variant='h5' color={'#41424E'}>
                            {field.fieldName}
                          </Typography>
                        </StyledSelectLabel>
                        <StyledFilterSelect
                          multipleStyle={true}
                          multiple
                          onChange={(event, newValue) =>
                            handleField(newValue, field)
                          }
                          noOptionsText={getLocalString('labels', 'notFound')}
                          options={field.fieldDefinition.values}
                          getOptionLabel={option => option.valueName || ''}
                          disableClearable
                          disablePortal
                          value={
                            !field.changed &&
                            field.fieldDefinition.values.filter(
                              item => item.isChecked,
                            ).length > 0
                              ? field.fieldDefinition.values.filter(
                                  item => item.isChecked,
                                )
                              : field.valueName
                                ? field.valueName
                                : []
                          }
                          renderValue={(value, getTagProps) =>
                            value.map((option, index) => (
                              <StyledChip
                                {...getTagProps({ index })}
                                key={index}
                                deleteIcon={
                                  <DeleteIcon
                                    width={10}
                                    height={10}
                                    viewBox='0 0 10 10'
                                  />
                                }
                                variant='outlined'
                                label={option.valueName}
                              />
                            ))
                          }
                          renderOption={(props, option, { selected }) => (
                            <li {...props} key={option.valueId}>
                              <Checkbox
                                icon=<CheckboxIcon
                                  width='12'
                                  height='12'
                                  viewBox='0 0 12 12'
                                />
                                checkedIcon=<CheckboxIconChecked
                                  width='12'
                                  height='12'
                                  viewBox='0 0 12 12'
                                />
                                style={{ marginRight: 8 }}
                                checked={selected}
                              />
                              {option.valueName}
                            </li>
                          )}
                          renderInput={params => (
                            <TextField
                              {...params}
                              placeholder={getLocalString('labels', 'input')}
                            />
                          )}
                          slots={{
                            popper: props => (
                              <Popper
                                {...props}
                                sx={{ maxWidth: '100%!important' }}
                                placement='bottom-start'
                              ></Popper>
                            ),
                          }}
                          popupIcon={
                            <ChevronIcon direction='down'></ChevronIcon>
                          }
                        />
                      </FormControl>
                    </StyledBlockWrapper>
                  </Grid>
                ))}
            </Grid>
          </Grid>
        </Page>
      )}
    </>
  );
};
export default TimesheetsSettings;
